/**
 * AiFreshnessPanel — surfaces whether a run's AI artefacts (Target Operating
 * Process + Board Q&A) were generated with the current prompt version, and
 * offers a one-click "Regenerate AI artefacts" that refreshes BOTH together,
 * so stale generations never slip into a client pack.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function AiFreshnessPanel({ runId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/assessments/runs/${runId}/ai-freshness`);
      setData(res.data);
      return res.data;
    } catch {
      setData(null);
    }
  }, [runId]);

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/assessments/runs/${runId}/regenerate-ai`);
      toast.success("Refreshing the Process Model + Board Q&A… (~2–3 min, runs in the background)");
      // poll freshness until both report the current version
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d && !d.any_stale && d.process_model?.has_ai && d.board_qa?.has_ai) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(false);
          toast.success("AI artefacts refreshed to the current prompt version");
        }
      }, 8000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Failed";
      toast.error(`Regenerate failed: ${msg}`);
      setBusy(false);
    }
  };

  if (!data || !data.any_present) return null;

  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const pmAt = fmt(data.process_model.generated_at);
  const bqAt = fmt(data.board_qa.generated_at);

  const stale = data.any_stale;
  return (
    <div className="ek-card p-4" data-testid="ai-freshness-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1A1A1F]">
          <Sparkles size={14} className="text-[#7737F6]" />
          AI artefact freshness
        </div>
        {stale ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#FCEDE8] text-[#B7472A] text-[11px] font-semibold px-2 py-0.5" data-testid="ai-stale-badge">
            <AlertTriangle size={11} /> Stale
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5EE] text-[#1E7A4D] text-[11px] font-semibold px-2 py-0.5" data-testid="ai-fresh-badge">
            <CheckCircle2 size={11} /> Current
          </span>
        )}
      </div>
      <p className="mt-2 text-[12px] leading-relaxed text-[#555560]">
        {stale
          ? "The stored Process Model and/or Board Q&A pre-date the current prompts. Regenerate before issuing a client pack so their text reflects the latest data and rules."
          : "The Process Model and Board Q&A were generated with the current prompt version."}
      </p>
      <ul className="mt-2 text-[11px] text-[#777783] space-y-1">
        <li>
          <span className="font-medium text-[#555560]">Process Model:</span>{" "}
          {data.process_model.has_ai
            ? <>{data.process_model.stored_version}{data.process_model.stale ? ` → ${data.process_model.current_version} (stale)` : " (current)"}{pmAt ? ` · generated ${pmAt}` : ""}</>
            : "not generated"}
        </li>
        <li>
          <span className="font-medium text-[#555560]">Board Q&A:</span>{" "}
          {data.board_qa.has_ai
            ? <>{data.board_qa.stored_version}{data.board_qa.stale ? ` → ${data.board_qa.current_version} (stale)` : " (current)"}{bqAt ? ` · generated ${bqAt}` : ""}</>
            : "not generated"}
        </li>
      </ul>
      <button
        type="button"
        onClick={regenerate}
        disabled={busy}
        className="ek-btn ek-btn-primary w-full mt-3 text-[13px] disabled:opacity-50"
        data-testid="regenerate-ai-btn"
      >
        <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
        {busy ? "Regenerating…" : "Regenerate AI artefacts"}
      </button>
    </div>
  );
}
